import { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { TRACK_SUGGESTION_MAP } from './constants';
import type {
  AdvancedSearchField,
  RecentlyOpenedObjectType,
  SearchAnalyticsContextType,
  TrackActionedOnResultsParams,
  TrackClearedFilterType,
  TrackDateFilterValue,
  TrackLocationFilterSelectedParams,
  TrackObjectTypeValue,
  TrackSearchExitedParams,
  TrackSearchFilterSelectedParams,
  TrackSearchNavigateAdvancedParams,
  TrackSearchNoResultsParams,
  TrackSearchOpenedSource,
  TrackSearchQueryInitiatedParams,
  TrackSearchResultListScrolledParams,
  TrackSearchResultsClickedParams,
  TrackSearchResultsDisplayedParams,
  TrackSearchResultsInspectedParams,
  TrackSearchScope,
  TrackSearchWebSource,
  TrackSortOrderValue,
  TrackTabSelected,
  TrackUserFilterSelectedParams,
} from './types';
import {
  getExitMethod,
  getFilterTypes,
  getTimeElapsedInSeconds,
} from './utils';

const ERROR_MESSAGE = 'must be used within a SearchAnalyticsProvider';

const SearchAnalyticsContext = createContext<SearchAnalyticsContextType | null>(
  null
);

export function SearchAnalyticsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const track = useTrackUnsafe();
  /**
   * sessionId - A unique identifier for the search session
   * initialized when search is opened, and reset when search is closed
   */
  const searchSessionId = useRef<string | null>(null);
  /**
   * searchQuery - The search query at the time the event took place
   */
  const searchQuery = useRef('');
  /**
   * searchOpenedAt - The time at which search is initialized
   * Allows us to calculate the delta between when search is opened and events that take place
   */
  const searchOpenedAt = useRef(0);
  /**
   * searchInitiatedAt - The time at which the last search query was initiated
   * Allows us to calculate the delta between when the last search query was initiated and events that take place
   */
  const searchInitiatedAt = useRef(0);
  /**
   * searchResultsDisplayedAt - The time at which the search results are displayed
   * Allows us to calculate the delta between when the search results are displayed and events that take place
   */
  const searchResultsDisplayedAt = useRef(0);
  /**
   * Set right before navigating to a search result. When the search page
   * unmounts as a result of that navigation, this suppresses the otherwise
   * automatic account-search-exited event — that event should only reflect
   * the user abandoning search (Home, Inbox, browser back), not an
   * intentional click-through to a result.
   */
  const isNavigatingToResult = useRef(false);

  /**
   * A new search session is initialized when search is opened
   */
  const initializeSearchSession = useCallback(() => {
    searchSessionId.current = crypto.randomUUID();
    searchOpenedAt.current = performance.now();
  }, []);

  /**
   * Resets the search session when the search is closed
   */
  const resetSearchSession = useCallback(() => {
    searchOpenedAt.current = 0;
    searchInitiatedAt.current = 0;
    searchResultsDisplayedAt.current = 0;
    searchQuery.current = '';
    searchSessionId.current = null;
  }, []);

  const resetSearchInitiatedAt = useCallback(() => {
    searchInitiatedAt.current = 0;
  }, []);

  const setSearchQuery = useCallback((query: string) => {
    searchQuery.current = query;
  }, []);

  const setSearchInitiatedAt = useCallback((time: number) => {
    searchInitiatedAt.current = time;
  }, []);

  const setSearchOpenedAt = useCallback((time: number) => {
    searchOpenedAt.current = time;
  }, []);

  const setSearchResultsDisplayedAt = useCallback((time: number) => {
    searchResultsDisplayedAt.current = time;
  }, []);

  const markResultNavigation = useCallback(() => {
    isNavigatingToResult.current = true;
  }, []);

  const trackSearchOpened = useCallback(
    (source: TrackSearchOpenedSource) => {
      initializeSearchSession();
      track('account-search-opened', {
        source,
        session_id: searchSessionId.current,
      });
    },
    [track, initializeSearchSession]
  );

  const trackSearchResultsClicked = useCallback(
    ({
      daysSinceUploaded,
      filterApplied,
      matchType = [],
      resultName,
      resultPosition,
      resultSource,
      resultType,
      searchQuery: query,
      source = 'SearchResult',
      tabSelected,
      webSource = 'Modal',
    }: TrackSearchResultsClickedParams) => {
      track('account-search-result-clicked', {
        days_since_uploaded: daysSinceUploaded,
        filter_applied: filterApplied,
        match_type: matchType,
        result_name: resultName,
        result_position: resultPosition,
        result_source: resultSource,
        result_tab_selected: tabSelected,
        result_type: resultType,
        search_query: query,
        session_id: searchSessionId.current,
        source,
        time_to_click: getTimeElapsedInSeconds(
          searchInitiatedAt.current,
          performance.now()
        ),
        web_source: webSource,
      });
    },
    [track]
  );

  const trackSearchResultsInspected = useCallback(
    ({
      resultPosition,
      resultType,
      objectName,
      resultSource,
      source = 'GlobalSearch',
      matchType = [],
      tabSelected,
      webSource = 'Modal',
    }: TrackSearchResultsInspectedParams) => {
      track('account-search-result-inspected', {
        result_position: resultPosition,
        result_type: resultType,
        search_query: searchQuery.current,
        object_name: objectName,
        match_type: matchType,
        time_to_click: getTimeElapsedInSeconds(
          searchResultsDisplayedAt.current,
          performance.now()
        ),
        session_id: searchSessionId.current,
        result_source: resultSource,
        source,
        result_tab_selected: tabSelected,
        web_source: webSource,
      });
    },
    [track]
  );

  const trackSearchExited = useCallback(
    ({ event, webSource = 'Modal' }: TrackSearchExitedParams) => {
      if (isNavigatingToResult.current) {
        isNavigatingToResult.current = false;
        return;
      }
      track('account-search-exited', {
        exit_method: getExitMethod(event?.type as 'keydown' | 'mousedown'),
        time_spent_on_search: getTimeElapsedInSeconds(
          searchOpenedAt.current,
          performance.now()
        ),
        session_id: searchSessionId.current,
        web_source: webSource,
      });
    },
    [track]
  );

  const trackSearchQueryInitiated = useCallback(
    ({
      filterApplied,
      searchQuery: query,
      tabSelected,
      webSource = 'Modal',
    }: TrackSearchQueryInitiatedParams) => {
      track('account-search-query-initiated', {
        character_count: query.length,
        filter_applied: filterApplied,
        result_tab_selected: tabSelected,
        search_query: query,
        session_id: searchSessionId.current,
        web_source: webSource,
      });
    },
    [track]
  );

  const trackSearchQueryCleared = useCallback(
    (
      tabSelected?: TrackTabSelected,
      webSource: TrackSearchWebSource = 'Modal'
    ) => {
      track('account-search-query-cleared', {
        session_id: searchSessionId.current,
        result_tab_selected: tabSelected,
        web_source: webSource,
      });
    },
    [track]
  );

  const trackSearchNoResults = useCallback(
    ({
      filterApplied,
      searchQuery: query,
      searchScope,
      webSource,
    }: TrackSearchNoResultsParams) => {
      track('account-search-no-results', {
        filter_applied: filterApplied,
        search_query: query,
        search_scope: searchScope,
        session_id: searchSessionId.current,
        web_source: webSource,
      });
    },
    [track]
  );

  const trackSearchResultsDisplayed = useCallback(
    ({
      filterApplied,
      isMediaIntelligenceResultsDisplayed,
      isStandardResultsDisplayed,
      searchQuery: query,
      searchScope,
      tabSelected,
      webSource = 'Modal',
    }: TrackSearchResultsDisplayedParams) => {
      track('account-search-results-displayed', {
        filter_applied: filterApplied,
        is_media_intelligence_results_displayed:
          isMediaIntelligenceResultsDisplayed,
        is_standard_results_displayed: isStandardResultsDisplayed,
        result_tab_selected: tabSelected,
        search_query: query,
        search_scope: searchScope,
        session_id: searchSessionId.current,
        web_source: webSource,
      });
    },
    [track]
  );

  const trackProjectScopeSelected = useCallback(
    (scope: TrackSearchScope) => {
      track(
        scope === 'CurrentProject'
          ? 'project-search-current-project-selected'
          : 'project-search-all-projects-selected',
        {
          session_id: searchSessionId.current,
        }
      );
    },
    [track]
  );

  const trackFilterSelected = useCallback(
    ({
      filter,
      filtersMap,
      webSource = 'Modal',
      locationScope = [],
      projectCount,
      projectId,
      workspaceCount,
      workspaceId,
    }: TrackSearchFilterSelectedParams) => {
      track('account-search-filter-selected', {
        filter_type: getFilterTypes(filtersMap, filter),
        session_id: searchSessionId.current,
        web_source: webSource,
        location_scope: locationScope,
        project_count: projectCount,
        project_id: projectId,
        workspace_count: workspaceCount,
        workspace_id: workspaceId,
      });
    },
    [track]
  );

  const trackObjectFilterSelected = useCallback(
    (filterType: TrackObjectTypeValue[]) => {
      track('account-search-object-filter-selected', {
        filter_type: filterType,
        session_id: searchSessionId.current,
        web_source: 'FullScreen',
      });
    },
    [track]
  );

  const trackDeletedAssetFilterSelected = useCallback(() => {
    track('account-search-deleted-asset-filter-selected', {
      session_id: searchSessionId.current,
      web_source: 'FullScreen',
    });
  }, [track]);

  const trackLocationFilterSelected = useCallback(
    ({
      filterType,
      folderId,
      projectId,
      workspaceId,
    }: TrackLocationFilterSelectedParams) => {
      track('account-search-location-filter-selected', {
        filter_type: filterType,
        folder_id: folderId,
        project_id: projectId,
        session_id: searchSessionId.current,
        web_source: 'FullScreen',
        workspace_id: workspaceId,
      });
    },
    [track]
  );

  const trackUserFilterSelected = useCallback(
    ({ count, filterType }: TrackUserFilterSelectedParams) => {
      track('account-search-user-filter-selected', {
        count,
        filter_type: filterType,
        session_id: searchSessionId.current,
        web_source: 'FullScreen',
      });
    },
    [track]
  );

  const trackDateFilterSelected = useCallback(
    (filterType: TrackDateFilterValue) => {
      track('account-search-date-filter-selected', {
        filter_type: filterType,
        session_id: searchSessionId.current,
        web_source: 'FullScreen',
      });
    },
    [track]
  );

  const trackFilterCleared = useCallback(
    (filterType: TrackClearedFilterType) => {
      track('account-search-filter-cleared', {
        filter_type: filterType,
        session_id: searchSessionId.current,
        web_source: 'FullScreen',
      });
    },
    [track]
  );

  const trackSortApplied = useCallback(
    (sortOrder: TrackSortOrderValue) => {
      track('account-search-sort-applied', {
        session_id: searchSessionId.current,
        sort_order: sortOrder,
        web_source: 'FullScreen',
      });
    },
    [track]
  );

  const trackRecentSearchSelected = useCallback(
    (query: string, webSource: TrackSearchWebSource = 'Modal') => {
      track('account-search-recent-search-selected', {
        search_query: query,
        session_id: searchSessionId.current,
        web_source: webSource,
      });
    },
    [track]
  );

  const trackRecentlyOpenedSelected = useCallback(
    (type: RecentlyOpenedObjectType, name: string) => {
      track('account-search-recently-opened-selected', {
        type,
        name,
        session_id: searchSessionId.current,
      });
    },
    [track]
  );

  const trackActionedOnResults = useCallback(
    ({
      action,
      assetCount,
      commentOption,
      filterApplied,
      folderCount,
      isBulkOperation,
      matchType = [],
      resultPosition,
      resultSource,
      resultType,
      webSource = 'Modal',
    }: TrackActionedOnResultsParams) => {
      track('account-search-actioned-on-result', {
        action,
        asset_count: assetCount,
        comment_option: commentOption,
        filter_applied: filterApplied,
        folder_count: folderCount,
        is_bulk_operation: isBulkOperation,
        match_type: matchType,
        result_position: resultPosition,
        result_source: resultSource,
        result_type: resultType,
        session_id: searchSessionId.current,
        web_source: webSource,
      });
    },
    [track]
  );

  const trackStandardTabSelected = useCallback(
    (webSource: TrackSearchWebSource = 'Modal') => {
      track('account-search-standard-selected', {
        session_id: searchSessionId.current,
        web_source: webSource,
      });
    },
    [track]
  );

  const trackAiTabSelected = useCallback(
    (webSource: TrackSearchWebSource = 'Modal') => {
      track('account-search-ai-selected', {
        session_id: searchSessionId.current,
        web_source: webSource,
      });
    },
    [track]
  );

  const trackSeeStandardResultsClicked = useCallback(
    (webSource: TrackSearchWebSource = 'Modal') => {
      track('account-search-see-standard-cta-clicked', {
        session_id: searchSessionId.current,
        web_source: webSource,
      });
    },
    [track]
  );

  const trackSeeAiResultsClicked = useCallback(
    (webSource: TrackSearchWebSource = 'Modal') => {
      track('account-search-see-ai-cta-clicked', {
        session_id: searchSessionId.current,
        web_source: webSource,
      });
    },
    [track]
  );

  const trackAdvancedSearchNavigated = useCallback(
    ({
      webSource,
      buttonSource = 'ShowAllResults',
    }: TrackSearchNavigateAdvancedParams) => {
      track('advanced-search-navigated', {
        button_source: buttonSource,
        session_id: searchSessionId.current,
        web_source: webSource,
      });
    },
    [track]
  );

  const trackSearchResultListScrolled = useCallback(
    ({
      pageNum,
      tabSelected,
      searchQuery: query,
    }: TrackSearchResultListScrolledParams) => {
      track('account-search-result-list-scrolled', {
        page_num: pageNum,
        result_tab_selected: tabSelected,
        search_query: query,
        session_id: searchSessionId.current,
      });
    },
    [track]
  );

  const trackSearchQuerySuggestionClicked = useCallback(
    (
      suggestionType: AdvancedSearchField,
      webSource: TrackSearchWebSource = 'FullScreen'
    ) => {
      const suggestion = TRACK_SUGGESTION_MAP[suggestionType];
      track('account-search-query-suggestion-clicked', {
        session_id: searchSessionId.current,
        suggestion,
        web_source: webSource,
      });
    },
    [track]
  );

  const contextValue = useMemo(
    () => ({
      initializeSearchSession,
      markResultNavigation,
      resetSearchInitiatedAt,
      resetSearchSession,
      searchSessionIdRef: searchSessionId,
      setSearchInitiatedAt,
      setSearchOpenedAt,
      setSearchQuery,
      setSearchResultsDisplayedAt,
      trackActionedOnResults,
      trackAdvancedSearchNavigated,
      trackAiTabSelected,
      trackDateFilterSelected,
      trackDeletedAssetFilterSelected,
      trackFilterCleared,
      trackFilterSelected,
      trackLocationFilterSelected,
      trackObjectFilterSelected,
      trackProjectScopeSelected,
      trackRecentSearchSelected,
      trackRecentlyOpenedSelected,
      trackSearchExited,
      trackSearchNoResults,
      trackSearchOpened,
      trackSearchQueryCleared,
      trackSearchQueryInitiated,
      trackSearchQuerySuggestionClicked,
      trackSearchResultListScrolled,
      trackSearchResultsClicked,
      trackSearchResultsDisplayed,
      trackSearchResultsInspected,
      trackSeeAiResultsClicked,
      trackSeeStandardResultsClicked,
      trackSortApplied,
      trackStandardTabSelected,
      trackUserFilterSelected,
    }),
    [
      initializeSearchSession,
      markResultNavigation,
      resetSearchInitiatedAt,
      resetSearchSession,
      setSearchInitiatedAt,
      setSearchOpenedAt,
      setSearchQuery,
      setSearchResultsDisplayedAt,
      trackActionedOnResults,
      trackAdvancedSearchNavigated,
      trackAiTabSelected,
      trackDateFilterSelected,
      trackDeletedAssetFilterSelected,
      trackFilterCleared,
      trackFilterSelected,
      trackLocationFilterSelected,
      trackObjectFilterSelected,
      trackProjectScopeSelected,
      trackRecentSearchSelected,
      trackRecentlyOpenedSelected,
      trackSearchExited,
      trackSearchNoResults,
      trackSearchOpened,
      trackSearchQueryCleared,
      trackSearchQueryInitiated,
      trackSearchQuerySuggestionClicked,
      trackSearchResultListScrolled,
      trackSearchResultsClicked,
      trackSearchResultsDisplayed,
      trackSearchResultsInspected,
      trackSeeAiResultsClicked,
      trackSeeStandardResultsClicked,
      trackSortApplied,
      trackStandardTabSelected,
      trackUserFilterSelected,
    ]
  );

  return (
    <SearchAnalyticsContext.Provider value={contextValue}>
      {children}
    </SearchAnalyticsContext.Provider>
  );
}

// Individual hooks for each tracking function to prevent unnecessary re-renders
export function useTrackSearchOpened() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useTrackSearchOpened ${ERROR_MESSAGE}`);
  }
  return context.trackSearchOpened;
}

export function useTrackSearchExited() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useTrackSearchExited ${ERROR_MESSAGE}`);
  }
  return context.trackSearchExited;
}

export function useTrackSearchQueryInitiated() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useTrackSearchQueryInitiated ${ERROR_MESSAGE}`);
  }
  return context.trackSearchQueryInitiated;
}

export function useTrackSearchQueryCleared() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useTrackSearchQueryCleared ${ERROR_MESSAGE}`);
  }
  return context.trackSearchQueryCleared;
}

export function useTrackSearchNoResults() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useTrackSearchNoResults ${ERROR_MESSAGE}`);
  }
  return context.trackSearchNoResults;
}

export function useTrackSearchResultsDisplayed() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useTrackSearchResultsDisplayed ${ERROR_MESSAGE}`);
  }
  return context.trackSearchResultsDisplayed;
}

export function useTrackSearchResultsClicked() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useTrackSearchResultsClicked ${ERROR_MESSAGE}`);
  }
  return context.trackSearchResultsClicked;
}

export function useTrackFilterSelected() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useTrackFilterSelected ${ERROR_MESSAGE}`);
  }
  return context.trackFilterSelected;
}

export function useTrackObjectFilterSelected() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useTrackObjectFilterSelected ${ERROR_MESSAGE}`);
  }
  return context.trackObjectFilterSelected;
}

export function useTrackDeletedAssetFilterSelected() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useTrackDeletedAssetFilterSelected ${ERROR_MESSAGE}`);
  }
  return context.trackDeletedAssetFilterSelected;
}

export function useTrackLocationFilterSelected() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useTrackLocationFilterSelected ${ERROR_MESSAGE}`);
  }
  return context.trackLocationFilterSelected;
}

export function useTrackUserFilterSelected() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useTrackUserFilterSelected ${ERROR_MESSAGE}`);
  }
  return context.trackUserFilterSelected;
}

export function useTrackDateFilterSelected() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useTrackDateFilterSelected ${ERROR_MESSAGE}`);
  }
  return context.trackDateFilterSelected;
}

export function useTrackFilterCleared() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useTrackFilterCleared ${ERROR_MESSAGE}`);
  }
  return context.trackFilterCleared;
}

export function useTrackSortApplied() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useTrackSortApplied ${ERROR_MESSAGE}`);
  }
  return context.trackSortApplied;
}

export function useTrackRecentSearchSelected() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useTrackRecentSearchSelected ${ERROR_MESSAGE}`);
  }
  return context.trackRecentSearchSelected;
}

export function useTrackRecentlyOpenedSelected() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useTrackRecentlyOpenedSelected ${ERROR_MESSAGE}`);
  }
  return context.trackRecentlyOpenedSelected;
}

export function useTrackSearchResultsInspected() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useTrackSearchResultsInspected ${ERROR_MESSAGE}`);
  }
  return context.trackSearchResultsInspected;
}

export function useTrackActionedOnResults() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useTrackActionedOnResults ${ERROR_MESSAGE}`);
  }
  return context.trackActionedOnResults;
}

export function useTrackProjectScopeSelected() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useTrackProjectScopeSelected ${ERROR_MESSAGE}`);
  }
  return context.trackProjectScopeSelected;
}

export function useTrackStandardTabSelected() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useTrackStandardTabSelected ${ERROR_MESSAGE}`);
  }
  return context.trackStandardTabSelected;
}

export function useTrackAiTabSelected() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useTrackAiTabSelected ${ERROR_MESSAGE}`);
  }
  return context.trackAiTabSelected;
}

export function useTrackSeeStandardResultsClicked() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useTrackSeeStandardResultsClicked ${ERROR_MESSAGE}`);
  }
  return context.trackSeeStandardResultsClicked;
}

export function useTrackSeeAiResultsClicked() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useTrackSeeAiResultsClicked ${ERROR_MESSAGE}`);
  }
  return context.trackSeeAiResultsClicked;
}

export function useTrackAdvancedSearchNavigated() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useTrackAdvancedSearchNavigated ${ERROR_MESSAGE}`);
  }
  return context.trackAdvancedSearchNavigated;
}

export function useTrackSearchResultListScrolled() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useTrackSearchResultListScrolled ${ERROR_MESSAGE}`);
  }
  return context.trackSearchResultListScrolled;
}

export function useSearchSession() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useSearchSession ${ERROR_MESSAGE}`);
  }
  return {
    searchSessionIdRef: context.searchSessionIdRef,
    initializeSearchSession: context.initializeSearchSession,
    resetSearchSession: context.resetSearchSession,
    resetSearchInitiatedAt: context.resetSearchInitiatedAt,
    setSearchOpenedAt: context.setSearchOpenedAt,
    setSearchInitiatedAt: context.setSearchInitiatedAt,
    setSearchResultsDisplayedAt: context.setSearchResultsDisplayedAt,
    setSearchQuery: context.setSearchQuery,
  };
}

export function useTrackSearchQuerySuggestionClicked() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useTrackSearchQuerySuggestionClicked ${ERROR_MESSAGE}`);
  }
  return context.trackSearchQuerySuggestionClicked;
}

export function useMarkSearchResultNavigation() {
  const context = useContext(SearchAnalyticsContext);
  if (!context) {
    throw new Error(`useMarkSearchResultNavigation ${ERROR_MESSAGE}`);
  }
  return context.markResultNavigation;
}
